/**
 * Axes resolver — converts a 6-axis design-system selection into the
 * `themeTokens` shape consumed by `getIsolatedAppStyle()` in the renderer.
 *
 * Wire-up overview:
 *
 *   design-system.json::axes               (Studio Save flow output)
 *   { style, baseColor, theme,
 *     chart, radius, font }
 *           │
 *           ▼   axesToThemeTokens(axes)
 *   themeTokens = { colors, radius, fonts }
 *           │
 *           ▼   passed to AppV2 / preview-entry
 *   getIsolatedAppStyle(themeTokens)       (renderer runtime)
 *   → CSS variables on the App wrapper + iframe :root
 *
 * Before this resolver, `axes` was written to disk by the Studio Save flow
 * but the renderer ignored it — runtime themeTokens only came from the
 * legacy `themePath` (`resources/theme-default.json`). This module closes
 * the loop so saved axis selections actually drive rendering.
 */

import type { ThemeVars } from './shadcn-presets'
import { lookupPreset, type LookupPresetInput } from './shadcn-presets'

/**
 * Strict 6-axis shape persisted by the Studio Save API. Mirrors the validator
 * in `apps/cogo-studio/vite-plugins/design-system-save-api.ts::isAxesShape`.
 */
export interface DesignSystemAxes {
  style: string
  baseColor: string
  theme: string
  chart: string
  radius: string
  font: string
}

/**
 * Loose runtime guard for `axes` blocks read from disk. Only requires the
 * minimum keys (`style`, `baseColor`) that `lookupPreset` needs in order to
 * succeed; the rest fall through to defensive defaults inside `lookupPreset`.
 */
export function isPartialAxesShape(v: unknown): v is Partial<DesignSystemAxes> {
  if (!v || typeof v !== 'object') return false
  const o = v as Record<string, unknown>
  return typeof o.style === 'string' && typeof o.baseColor === 'string'
}

/**
 * Renderer-facing themeTokens shape. Matches the structure consumed by
 * `getIsolatedAppStyle()` in `@cogo/react-renderer/runtime/design-tokens`
 * (the contract is duplicated here, not imported, to keep this package
 * dependency-free).
 */
export interface AxesThemeTokens {
  /** CSS variable map keyed by the bare semantic name (e.g. `primary`, `card-foreground`). */
  colors: Record<string, string>
  /** Border-radius value (e.g. `0.625rem`). */
  radius?: string
  /** Per-slot font stacks. Slots are picked by the font preset's category. */
  fonts?: { sans?: string; mono?: string; heading?: string }
}

/**
 * The CSS variable name `radius` lives inside `cssVars.light` alongside the
 * color tokens. Strip it out and surface it on the top-level `radius` field
 * so it flows through `getIsolatedAppStyle`'s dedicated radius branch.
 */
function splitColorsAndRadius(vars: ThemeVars): { colors: Record<string, string>; radius?: string } {
  const colors: Record<string, string> = {}
  let radius: string | undefined
  for (const [key, value] of Object.entries(vars)) {
    if (key === 'radius') {
      radius = value
    } else {
      colors[key] = value
    }
  }
  return { colors, radius }
}

/**
 * Convert a 6-axis selection into themeTokens for the runtime resolver.
 *
 * Throws via `lookupPreset` if `style` or `baseColor` is unknown. Unknown
 * `theme` / `chart` / `font` are silently ignored (matches Studio behavior).
 */
export function axesToThemeTokens(axes: DesignSystemAxes | Partial<DesignSystemAxes>): AxesThemeTokens {
  const input: LookupPresetInput = {
    style: axes.style ?? 'luma',
    baseColor: axes.baseColor ?? 'zinc',
    theme: axes.theme,
    chartColor: axes.chart,
    radius: axes.radius,
    font: axes.font,
  }
  const result = lookupPreset(input)
  const { colors, radius } = splitColorsAndRadius(result.cssVars.light)
  return {
    colors,
    radius,
    fonts: result.fonts,
  }
}

/**
 * Legacy direct-honor shape — the pre-axes `design-system.json` format still
 * used by ~55 templates on disk. Has no `axes` block, but carries literal
 * CSS-var-ready values (HSL triplet strings + rem radius + optional fonts).
 *
 * Shape seen in the wild (jewelry-new3 / bakery-shop-new3 / agency-studio-new3):
 *   {
 *     "colors": {
 *       "primary": "45 80% 48%",           // HSL channels (wrapped at runtime)
 *       "primary-foreground": "0 0% 100%",
 *       "accent": "45 50% 95%",
 *       "accent-foreground": "45 80% 20%",
 *       "ring": "45 80% 48%"
 *     },
 *     "radius": "0.5rem"
 *   }
 *
 * These fields match the contract consumed by `getIsolatedAppStyle()` in the
 * renderer, so we pass them through unchanged (modulo hex→HSL conversion
 * happens downstream in `getScopedTokenStyle`).
 */
interface LegacyDirectDesignSystem {
  colors?: Record<string, string>
  radius?: string
  /**
   * Per-slot font stacks, same shape as `AxesThemeTokens.fonts`. Rare in the
   * wild as of 2026-04-18, but honored for forward-compat with hand-authored
   * design-system.json files.
   */
  fonts?: { sans?: string; mono?: string; heading?: string }
  /** Legacy single-family override, same meaning as in `getIsolatedAppStyle`. */
  fontFamily?: string
}

/**
 * Runtime guard: design-system blob carries at least one legacy direct field
 * (`colors.primary` or `radius`) we can honor. We require `primary` as the
 * minimum signal — a `colors` map without it (or without any keys at all)
 * doesn't carry enough information to reflect the template's identity and
 * would drift toward the default theme either way.
 */
function hasLegacyDirectShape(ds: Record<string, unknown>): boolean {
  const colors = ds.colors as Record<string, unknown> | undefined
  const hasPrimary = colors && typeof colors === 'object' && typeof colors.primary === 'string'
  const hasRadius = typeof ds.radius === 'string'
  return Boolean(hasPrimary || hasRadius)
}

/**
 * Pure helper — build `AxesThemeTokens` directly from the legacy literal
 * fields, no preset-registry lookup. Leaves input strings as-is; downstream
 * `getScopedTokenStyle` normalizes HSL channels / hex values into complete
 * CSS colors, so this stays format-agnostic.
 */
export function legacyDirectToThemeTokens(ds: LegacyDirectDesignSystem): AxesThemeTokens {
  const colors: Record<string, string> = ds.colors ? { ...ds.colors } : {}
  const tokens: AxesThemeTokens = { colors }
  if (typeof ds.radius === 'string' && ds.radius.length > 0) {
    tokens.radius = ds.radius
  }
  if (ds.fonts && typeof ds.fonts === 'object') {
    const { sans, mono, heading } = ds.fonts
    tokens.fonts = {
      ...(sans ? { sans } : {}),
      ...(mono ? { mono } : {}),
      ...(heading ? { heading } : {}),
    }
  }
  return tokens
}

/**
 * Try to derive themeTokens from a `design-system.json` blob. Uses a 3-layer
 * fallback chain:
 *
 *   1. `axes` block populated → derive tokens via `@cogo/design-presets`
 *      preset-registry lookup (Studio Save flow output, post-2026-04-16).
 *   2. Legacy direct-honor — `colors` / `radius` / `fonts` literal fields
 *      present → pass through to `AxesThemeTokens` unchanged. Covers the
 *      ~55 pre-axes templates on disk where `axes` is null but
 *      `colors.primary` + `radius` faithfully represent the design.
 *   3. [DS 2.0 TODO] LLM-derive — when both layers 1+2 miss, the bind/retrieve
 *      paradigm in `packages/cogo-binder/` will eventually call an LLM to
 *      infer an `axes` selection (or a direct token map) from the template's
 *      content/thumbnail, then re-enter this function. Hook point: new branch
 *      after the `hasLegacyDirectShape` check returns false — would invoke
 *      `cogo-binder`'s retrieval+bind pipeline synchronously from the loader
 *      (or async+cache via a background job). Not implemented in this patch.
 *
 * Returns `null` only when all three layers miss, leaving the caller to fall
 * back to the legacy `themePath` / `theme-default.json` path.
 *
 * Designed for defensive use in the loader: never throws. Logs a single
 * dev-warning when an `axes` block is present but malformed.
 *
 * @todo DS 2.0 Layer 3 — wire `cogo-binder` retrieval+bind here so templates
 * with neither axes nor legacy direct fields get an LLM-derived theme instead
 * of `theme-default.json` generic fallback. See `docs/design-system/*` for
 * the retrieval+bind contract.
 */
export function tryDeriveThemeTokensFromDesignSystem(
  designSystem: unknown,
): AxesThemeTokens | null {
  if (!designSystem || typeof designSystem !== 'object') return null
  const root = designSystem as Record<string, unknown>

  // Layer 1: axes-first (Studio Save flow output) — axes wins when present.
  const axes = root.axes
  if (axes !== undefined) {
    if (!isPartialAxesShape(axes)) {
      if (typeof console !== 'undefined' && typeof console.warn === 'function') {
        console.warn(
          '[cogo/design-presets] design-system.json::axes is present but malformed (need at least `style` + `baseColor`); attempting legacy direct-honor fallback.',
        )
      }
      // Fall through to layer 2 — malformed axes shouldn't poison a template
      // that also has usable legacy direct fields.
    } else {
      try {
        return axesToThemeTokens(axes)
      } catch (err) {
        if (typeof console !== 'undefined' && typeof console.warn === 'function') {
          console.warn(
            '[cogo/design-presets] axesToThemeTokens failed; attempting legacy direct-honor fallback.',
            err,
          )
        }
        // Fall through to layer 2 — unknown style/baseColor shouldn't kill
        // the page when legacy fields are also present.
      }
    }
  }

  // Layer 2: legacy direct-honor — pass literal `colors` / `radius` / `fonts`
  // through unchanged. Fixes ~55 pre-axes templates that previously fell
  // through to `theme-default.json` and rendered with the generic violet
  // default (2026-04-18: `.planning/cogo-starter-prod-deploy-verify-2026-04-18.md`).
  if (hasLegacyDirectShape(root)) {
    return legacyDirectToThemeTokens(root as LegacyDirectDesignSystem)
  }

  // Layer 3: LLM-derive (DS 2.0 retrieval+bind) — see JSDoc `@todo` above.
  // Returning null keeps the existing fallback to `theme-default.json` intact.
  return null
}
